/**
 * FieldPickerPopover Utilities
 *
 * Common utility functions for the field picker component.
 */

import type { PickerField, PickerFieldGroup } from './types';

/**
 * Filter fields by search query
 * Matches against field name, label, group, and description
 */
export function filterFields(fields: PickerField[], query: string): PickerField[] {
  if (!query.trim()) return fields;

  const normalizedQuery = query.toLowerCase().trim();

  return fields.filter((field) => {
    const searchable = [field.name, field.label, field.group, field.description]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return searchable.includes(normalizedQuery);
  });
}

/**
 * Group fields by their group key
 */
export function groupFieldsByKey(
  fields: PickerField[],
  groups: PickerFieldGroup[]
): Map<string, PickerField[]> {
  const result = new Map<string, PickerField[]>();

  // Initialize with empty arrays for all groups
  for (const group of groups) {
    result.set(group.key, []);
  }

  // Group fields
  for (const field of fields) {
    const groupKey = field.group;
    const existing = result.get(groupKey) || [];
    existing.push(field);
    result.set(groupKey, existing);
  }

  return result;
}

/**
 * Sort groups by order, then alphabetically
 */
export function sortGroups(groups: PickerFieldGroup[]): PickerFieldGroup[] {
  return [...groups].sort((a, b) => {
    // First by order (undefined = Infinity)
    const orderA = a.order ?? Infinity;
    const orderB = b.order ?? Infinity;
    if (orderA !== orderB) return orderA - orderB;

    // Then alphabetically by label
    return a.label.localeCompare(b.label);
  });
}

/**
 * Get visible fields in a flat list (for keyboard navigation)
 * Respects collapsed groups
 */
export function getFlatFieldList(
  fields: PickerField[],
  groups: PickerFieldGroup[],
  collapsedGroups: Set<string>,
  searchQuery: string
): PickerField[] {
  const filtered = filterFields(fields, searchQuery);
  const grouped = groupFieldsByKey(filtered, groups);
  const sortedGroups = sortGroups(groups);

  // When searching, all groups are expanded
  const isSearching = searchQuery.trim().length > 0;

  const result: PickerField[] = [];

  for (const group of sortedGroups) {
    const groupFields = grouped.get(group.key) || [];
    if (groupFields.length === 0) continue;

    // Skip collapsed groups (unless searching)
    if (!isSearching && collapsedGroups.has(group.key)) continue;

    result.push(...groupFields);
  }

  return result;
}

/**
 * Build field indices map for keyboard navigation
 */
export function buildFieldIndices(fields: PickerField[]): Map<string, number> {
  const indices = new Map<string, number>();
  fields.forEach((field, index) => {
    indices.set(field.name, index);
  });
  return indices;
}

/**
 * Get field by name
 */
export function getFieldByName(
  fields: PickerField[],
  name: string
): PickerField | undefined {
  return fields.find((f) => f.name === name);
}

/**
 * Get group by key
 */
export function getGroupByKey(
  groups: PickerFieldGroup[],
  key: string
): PickerFieldGroup | undefined {
  return groups.find((g) => g.key === key);
}

/**
 * Count fields in a group
 */
export function countFieldsInGroup(fields: PickerField[], groupKey: string): number {
  return fields.filter((f) => f.group === groupKey).length;
}

/**
 * Get display label for a field value
 * Handles both simple values and predefined select values
 */
export function getFieldValueLabel(
  field: PickerField | undefined,
  value: string
): string {
  if (!field) return value;

  if (field.values) {
    const option = field.values.find((v) => v.name === value);
    if (option) return option.label;
  }

  return value;
}
